if (!customElements.get('l-bundle-picker')) {
  const norm = (s) => (s == null ? '' : String(s).trim().toLowerCase());

  class LBundlePicker extends HTMLElement {
    constructor() {
      super();
      this.state = {};
      this.data = null;
      this.optionValues = {};
      this.optionOwners = {};
      this.formEl = null;
      this.submitBtn = null;
      this.bundleGroup = '';
      this._onChange = this._onChange.bind(this);
    }

    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._init(), { once: true });
      } else {
        this._init();
      }
    }

    _init() {
      this.data = this._parseData();
      if (!this.data || !this.data.children || !this.data.children.length) return;

      this.bundleGroup = String(this.data.bundleGroup || this.dataset.bundleGroup || '');
      const formId = this.dataset.formId;
      this.formEl = formId ? document.getElementById(formId) : null;
      this.submitBtn = this.formEl ? this.formEl.querySelector('[type="submit"]') : null;

      this._buildOptionMaps();
      this._initState();
      this._render();
      this.addEventListener('change', this._onChange);
      this._refresh();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
    }

    _parseData() {
      const el = this.querySelector('script[type="application/json"][data-bundle-picker-data]');
      if (!el || !el.textContent) return null;
      try {
        return JSON.parse(el.textContent.trim());
      } catch (_) {
        return null;
      }
    }

    _buildOptionMaps() {
      const order = (this.data.optionOrder || []).filter((n) => n && n.trim() !== '');
      this.data.optionOrder = order;

      const valuesByOpt = {};
      const ownersByOpt = {};
      const canonicalNames = {};

      for (const name of order) {
        valuesByOpt[name] = [];
        ownersByOpt[name] = [];
        canonicalNames[norm(name)] = name;
      }

      const mainVals = this.data.mainOptionValues || {};
      for (const [optName, vals] of Object.entries(mainVals)) {
        const canonical = canonicalNames[norm(optName)];
        if (!canonical || !Array.isArray(vals)) continue;
        for (const val of vals) {
          if (val != null && val !== '' && !valuesByOpt[canonical].some((x) => norm(x) === norm(val))) {
            valuesByOpt[canonical].push(val);
          }
        }
      }

      this.data.children.forEach((child, idx) => {
        const childOpts = (child.options || []).filter((n) => n && n.trim() !== '');
        child.options = childOpts;
        child._normToCanonical = {};
        for (const co of childOpts) {
          const canonical = canonicalNames[norm(co)];
          if (!canonical) continue;
          child._normToCanonical[norm(co)] = canonical;
          if (!ownersByOpt[canonical].includes(idx)) ownersByOpt[canonical].push(idx);
        }

        for (const v of child.variants) {
          const remapped = {};
          for (const [k, val] of Object.entries(v.optionValues || {})) {
            const canonical = canonicalNames[norm(k)];
            if (!canonical) continue;
            remapped[canonical] = val;
            const list = valuesByOpt[canonical];
            if (val != null && val !== '' && !list.some((x) => norm(x) === norm(val))) {
              list.push(val);
            }
          }
          v._values = remapped;
        }
      });

      this.optionValues = valuesByOpt;
      this.optionOwners = ownersByOpt;
    }

    _isValueSellableGivenState(opt, val, state) {
      const owners = this.optionOwners[opt] || [];
      if (!owners.length) return false;
      for (const idx of owners) {
        const child = this.data.children[idx];
        const ok = child.variants.some((v) => {
          if (!v.sellable) return false;
          if (norm(v._values[opt]) !== norm(val)) return false;
          for (const childOpt of child.options) {
            const canonical = child._normToCanonical[norm(childOpt)];
            if (!canonical || canonical === opt) continue;
            const want = state[canonical];
            if (want == null) continue;
            if (norm(v._values[canonical]) !== norm(want)) return false;
          }
          return true;
        });
        if (!ok) return false;
      }
      return true;
    }

    _initState() {
      const params = new URLSearchParams(window.location.search);
      const state = {};
      for (const opt of this.data.optionOrder) {
        const values = this.optionValues[opt] || [];
        const fromUrl = params.get(opt);
        let pick = null;
        if (fromUrl != null) {
          const matched = values.find((v) => norm(v) === norm(fromUrl));
          if (matched && this._isValueSellableGivenState(opt, matched, state)) {
            pick = matched;
          }
        }
        if (pick == null) {
          for (const val of values) {
            if (this._isValueSellableGivenState(opt, val, state)) {
              pick = val;
              break;
            }
          }
        }
        state[opt] = pick;
      }
      this.state = state;
    }

    _updateUrl() {
      const url = new URL(window.location.href);
      for (const opt of this.data.optionOrder) {
        const val = this.state[opt];
        if (val == null) url.searchParams.delete(opt);
        else url.searchParams.set(opt, val);
      }
      window.history.replaceState({}, '', url.toString());
    }

    _render() {
      const host = this.querySelector('[data-bundle-picker-options]');
      if (!host) return;
      const sectionId = this.dataset.sectionId;
      const parts = [];
      for (const opt of this.data.optionOrder) {
        const values = this.optionValues[opt] || [];
        if (!values.length) continue;
        parts.push(
          `<fieldset class="js product-form__input product-form__input--pill" data-bundle-option="${this._attr(opt)}">`
        );
        parts.push(
          `<legend class="form__label body-sm-fs fw-600 lh-12 uppercase">${this._html(opt)}</legend>`
        );
        values.forEach((val, i) => {
          const id = `bundle-${sectionId}-${this._slug(opt)}-${i}`;
          parts.push(
            `<input type="radio" id="${id}" name="bundle-${this._attr(opt)}" value="${this._attr(val)}" data-bundle-input data-bundle-option-name="${this._attr(opt)}">` +
              `<label for="${id}">${this._html(val)}</label>`
          );
        });
        parts.push(`</fieldset>`);
      }
      host.innerHTML = parts.join('');
    }

    _html(s) {
      return String(s == null ? '' : s).replace(
        /[&<>"']/g,
        (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
      );
    }
    _attr(s) {
      return this._html(s);
    }
    _slug(s) {
      return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    }

    _onChange(e) {
      const t = e.target;
      if (!(t instanceof HTMLInputElement) || !t.hasAttribute('data-bundle-input')) return;
      const opt = t.dataset.bundleOptionName;
      if (!opt) return;
      this.state[opt] = t.value;
      this._refresh();
    }

    _refresh() {
      this._applyAvailability();
      this._syncCheckedRadios();
      this._updateLineItems();
      this._updateSubmitAvailability();
      this._updatePagePrice();
      this._updateInventoryStatus();
      this._updateUrl();
    }

    _bundleStockState() {
      const matched = this._collectMatchedVariants();
      if (!matched.length) return null;

      let hasHardOutOfStock = false;
      let hasBackorder = false;

      for (const { variant } of matched) {
        if (!variant) return null;

        const isTracked = variant.inventoryManagement === 'shopify';
        const isOutOfStock = isTracked && Number(variant.inventoryQuantity) <= 0;
        const allowsBackorder = variant.inventoryPolicy === 'continue';

        if (!isOutOfStock) continue;
        if (allowsBackorder) hasBackorder = true;
        else hasHardOutOfStock = true;
      }

      if (hasHardOutOfStock) return 'out';
      if (hasBackorder) return 'backorder';
      return 'in';
    }

    _updateInventoryStatus() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;

      const state = this._bundleStockState();
      if (!state) return;

      const i18n = this.data.i18n || {};
      const presentation = {
        in: { dotModifier: 'in-stock', label: i18n.inStock },
        backorder: { dotModifier: 'backorder', label: i18n.backorder },
        out: { dotModifier: 'out', label: i18n.outOfStock },
      }[state];
      if (!presentation) return;

      const blocks = document.querySelectorAll(`[id="Inventory-${sectionId}"]`);
      blocks.forEach((block) => {
        block.classList.remove('visibility-hidden');

        const dot = block.querySelector('.product__inventory-dot');
        if (dot) dot.className = `product__inventory-dot product__inventory-dot--${presentation.dotModifier}`;

        const label = block.querySelector('.product__inventory-status');
        if (label && presentation.label) label.textContent = presentation.label;
      });
    }

    _money(cents) {
      const useCurrency = this.data.currencyCodeEnabled;
      const fmt = useCurrency
        ? this.data.moneyWithCurrencyFormat || this.data.moneyFormat
        : this.data.moneyFormat;
      if (typeof window.l_formatMoney === 'function') {
        return window.l_formatMoney(cents, fmt || '');
      }
      const currency = this.data.currency || 'USD';
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
      } catch (_) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    _updatePagePrice() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;
      const blocks = document.querySelectorAll(`[id="price-${sectionId}"]`);
      if (!blocks.length) return;

      const matched = this._collectMatchedVariants();
      let total = 0;
      let compare = 0;
      let hasCompare = false;
      for (const m of matched) {
        if (!m.variant) return;
        const price = Number(m.variant.price || 0);
        const cmp = Number(m.variant.compareAtPrice || 0);
        total += price;
        if (cmp > price) hasCompare = true;
        compare += cmp > 0 ? cmp : price;
      }
      const onSale = hasCompare && compare > total;
      const totalStr = this._money(total);
      const compareStr = this._money(compare);

      blocks.forEach((block) => {
        const priceEl = block.querySelector('.price');
        if (!priceEl) return;
        priceEl.classList.toggle('price--on-sale', onSale);
        priceEl
          .querySelectorAll('.price__regular .price-item--regular')
          .forEach((el) => (el.textContent = totalStr));
        const saleVal = priceEl.querySelector('.price__sale .price-item--sale');
        if (saleVal) saleVal.textContent = totalStr;
        const saleCompare = priceEl.querySelector('.price__sale s.price-item--regular');
        if (saleCompare) saleCompare.textContent = compareStr;
      });
    }

    _applyAvailability() {
      for (const opt of this.data.optionOrder) {
        const inputs = this.querySelectorAll(`[data-bundle-option="${CSS.escape(opt)}"] [data-bundle-input]`);
        inputs.forEach((input) => {
          const sellable = this._isValueSellableGivenState(opt, input.value, this.state);
          input.classList.toggle('disabled', !sellable);
          input.disabled = !sellable;
        });
      }
    }

    _syncCheckedRadios() {
      for (const opt of this.data.optionOrder) {
        const want = this.state[opt];
        const inputs = this.querySelectorAll(`[data-bundle-option="${CSS.escape(opt)}"] [data-bundle-input]`);
        inputs.forEach((input) => {
          input.checked = norm(input.value) === norm(want);
        });
      }
    }

    _matchedVariantFor(child) {
      if (!child.options.length) return child.variants[0] || null;
      return (
        child.variants.find((v) => {
          if (!v.sellable) return false;
          for (const childOpt of child.options) {
            const canonical = child._normToCanonical[norm(childOpt)];
            if (!canonical) return false;
            const want = this.state[canonical];
            if (want == null) return false;
            if (norm(v._values[canonical]) !== norm(want)) return false;
          }
          return true;
        }) || null
      );
    }

    _collectMatchedVariants() {
      const out = [];
      for (const child of this.data.children) {
        const v = this._matchedVariantFor(child);
        out.push({ child, variant: v });
      }
      return out;
    }

    _updateLineItems() {
      if (!this.formEl) return;
      this.formEl.querySelectorAll('input[data-bundle-line-item]').forEach((el) => el.remove());

      const matched = this._collectMatchedVariants();
      const allValid =
        matched.length > 0 &&
        matched.every((m) => m.variant && m.variant.sellable) &&
        this.data.optionOrder.every((opt) => this.state[opt] != null);

      if (!allValid) return;

      const bundleId =
        this.bundleGroup +
        '_' +
        matched
          .map((m) => m.variant.id)
          .slice()
          .sort()
          .join('_');

      matched.forEach((m, i) => {
        this._appendHidden(`items[${i}][id]`, String(m.variant.id));
        this._appendHidden(`items[${i}][quantity]`, '1');
        this._appendHidden(`items[${i}][properties][_bundle_group]`, this.bundleGroup);
        this._appendHidden(`items[${i}][properties][_bundle_id]`, bundleId);
        this._appendHidden(`items[${i}][properties][_bundle_size]`, String(matched.length));
        if (this.data.bundleTitle) {
          this._appendHidden(`items[${i}][properties][_bundle_title]`, this.data.bundleTitle);
        }
        if (this.data.bundleImage) {
          this._appendHidden(`items[${i}][properties][_bundle_image]`, this.data.bundleImage);
        }
        if (this.data.bundleHandle) {
          this._appendHidden(`items[${i}][properties][_bundle_handle]`, this.data.bundleHandle);
        }
      });
    }

    _appendHidden(name, value) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      input.setAttribute('data-bundle-line-item', '');
      this.formEl.appendChild(input);
    }

    _updateSubmitAvailability() {
      if (!this.submitBtn) return;
      const matched = this._collectMatchedVariants();
      const allValid =
        matched.length > 0 &&
        matched.every((m) => m.variant && m.variant.sellable) &&
        this.data.optionOrder.every((opt) => this.state[opt] != null);
      this.submitBtn.toggleAttribute('disabled', !allValid);
      this.submitBtn.setAttribute('aria-disabled', String(!allValid));
    }
  }

  customElements.define('l-bundle-picker', LBundlePicker);
}
